import {
  AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig
} from 'axios'
import useGlobalStore from '@/stores/modules/global'
import StatusCodeType from '@/enum/StatusCodeType'
import {
  ACCOUNT_HTTP,
  DASHBOARD_CLIENT_TYPE,
  DEFAULT_COOKIE_MAX_AGE,
  REVOKE_URL
} from '@/utils/constants'
import AccountApiUrls from '@/api/account/endpoints'
import type {
  TAccountAccessRequestBody,
  TAccountAccessRenewBody,
  TAccountAccessRevokeBody,
  TAccountAccessResponse
} from '@/types/api-types/account'
import type { TApiResponse } from '@/types/api-types/TApiResponse'
import type { INetworkServiceProvider } from '@/types/api-types'
import { logError } from '@/utils/helpers/logger'
import {
  clearLocalStorageAndCookies,
  saveDataToCookieStorage,
  saveUserIdToLocalStorage
} from '@/utils/helpers'
import CookieStorageService from './CookieStorageService'

const setAccountState = (data: TAccountAccessResponse) => {
  const globalStore = useGlobalStore()
  globalStore.setAccountData(data)
}

const getAuthCookies = () => {
  const cookies = new CookieStorageService()
  return {
    accessToken: cookies.getData('accessToken'),
    refreshToken: cookies.getData('refreshToken'),
    expiringAt: cookies.getData('expiringAt')
  }
}

const handleTokens = (response: AxiosResponse) => {
  if (!response) return
  if (response.status === StatusCodeType.Success) {
    saveUserIdToLocalStorage(response.data.userId)
    saveDataToCookieStorage(response.data)
    setAccountState(response.data)
  }
}

export const clearStorageAndRefresh = (options?: any) => {
  clearLocalStorageAndCookies(options)
  // eslint-disable-next-line no-restricted-globals
  location.reload()
}

const updateRequestConfig = (requestConfig: AxiosRequestConfig) => {
  const { accessToken } = getAuthCookies()
  const updatedAuthorization = `Bearer ${accessToken}`
  return {
    ...requestConfig,
    headers: {
      ...requestConfig.headers,
      Authorization: updatedAuthorization
    }
  }
}

class AuthService {
  private requestProvider: INetworkServiceProvider

  constructor(requestProvider: INetworkServiceProvider) {
    this.requestProvider = requestProvider
    this.setupAuthInterceptors()
  }

  private setupAuthInterceptors() {
    this.requestProvider.service.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        AuthService.checkTokenExpirationAndRenewAuth(config.url).catch((error) => {
          const globalStore = useGlobalStore()
          globalStore.abortPendingRequests()
          clearStorageAndRefresh({ localStoragePreserveItems: ['version'] })
          logError(error, error)
        })
        return config
      },
      (error: AxiosError) => {
        logError(error.message, error)
      }
    )
    this.requestProvider.service.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const unauthorizedAccess = error.response?.status === StatusCodeType.Unauthorized
        const failedRequest = error.config as AxiosRequestConfig

        if (unauthorizedAccess && failedRequest.url === AccountApiUrls.RENEW()) {
          clearStorageAndRefresh({ localStoragePreserveItems: ['version'] })
          return Promise.reject(error)
        }

        const noRetryURLs =
          failedRequest.url === AccountApiUrls.RENEW() ||
          failedRequest.url === AccountApiUrls.REQUEST() ||
          failedRequest.url === AccountApiUrls.RESET_PASSWORD() ||
          failedRequest.url === AccountApiUrls.CONFIRM_NEW_PASSWORD() ||
          failedRequest.url?.includes(REVOKE_URL) ||
          failedRequest.url?.includes(ACCOUNT_HTTP)
        if (!unauthorizedAccess || noRetryURLs) {
          return Promise.reject(error)
        }
        return this.renewAuthPromise()
          .then((response) => {
            if (response?.status === StatusCodeType.Success) {
              const updatedConfig = updateRequestConfig(error.config as AxiosRequestConfig)
              const globalStore = useGlobalStore()
              globalStore.refreshAttempts = 0
              return this.requestProvider.service(updatedConfig)
            }
            return Promise.reject(error)
          })
          .catch((error) => error)
      }
    )
  }

  async renewAuthPromise() {
    const globalStore = useGlobalStore()
    if (!globalStore.isAttemptingRefresh) {
      globalStore.renewRequestPromise = this.checkRefreshAttemptsAndRenewAuth().catch((error) => {
        if (error instanceof AxiosError) {
          this.renewAuthPromise()
        } else {
          logError(error, error)
          clearStorageAndRefresh({ localStoragePreserveItems: ['version'] })
        }
      })
    }
    return globalStore.renewRequestPromise
  }

  static async checkTokenExpirationAndRenewAuth(url?: string) {
    const { accessToken, refreshToken, expiringAt } = getAuthCookies()
    const expirationTime = new Date(expiringAt).getTime()
    const currentTime = Date.now()
    const bufferTime = 5 * 60 * 1000
    const tokenAboutToExpire = currentTime + bufferTime >= expirationTime
    if (
      !accessToken &&
      url &&
      ![
        AccountApiUrls.REQUEST(),
        AccountApiUrls.RESET_PASSWORD(),
        AccountApiUrls.CONFIRM_NEW_PASSWORD()
      ].includes(url)
    ) {
      throw new Error('Tokens have expired. Logging user out...')
    }
  }

  async checkRefreshAttemptsAndRenewAuth(): Promise<any> {
    const globalStore = useGlobalStore()
    const { accessToken, refreshToken } = getAuthCookies()
    if (globalStore.refreshAttemptsExceeded) {
      globalStore.refreshAttempts = 0
      throw new Error('Refresh attempts exceeded')
    } else if (refreshToken && accessToken) {
      globalStore.refreshAttempts++
      globalStore.isAttemptingRefresh = true
      return this.renewAuth({
        accessToken,
        refreshToken,
        clientAppKey: DASHBOARD_CLIENT_TYPE
      }).finally(() => {
        globalStore.isAttemptingRefresh = false
      })
    } else {
      throw new Error('Tokens have expired')
    }
  }

  requestAuth(url: string, user: TAccountAccessRequestBody): TApiResponse {
    const form = new FormData()
    Object.entries(user).forEach(([key, value]) => {
      form.set(key, value)
    })
    const response = this.requestProvider.sendPostRequest(url, form).then((response) => {
      handleTokens(response)
      return response
    })
    return response
  }

  async renewAuth(user: TAccountAccessRenewBody): TApiResponse {
    const form = new FormData()
    Object.entries(user).forEach(([key, value]) => form.append(key, value))
    return this.requestProvider
      .sendPostRequest(AccountApiUrls.RENEW(), form, true)
      .then((response) => {
        handleTokens(response)
        return response
      })
  }

  async revokeAuth(url: string, user: TAccountAccessRevokeBody): TApiResponse {
    return this.requestProvider.sendPostRequest(url, user)
  }

  async revokeUserAuth(url: string): TApiResponse {
    return this.requestProvider.sendPostRequest(url)
  }
}

export default AuthService
